import { useMemo, useState } from 'react';
import { ChevronRight, Clock3, Gauge, Play, Sparkles, Star, Ticket, Trophy } from 'lucide-react';
import GameArtwork from './GameArtwork';
import { games } from '../games/registry';
import type { ArcadeSave } from '../platform/persistence';
import type { GameId } from '../platform/types';

interface PortalProps {
  save: ArcadeSave;
  onPlay: (id: GameId) => void;
}

const filters = ['All games', 'Quick action', 'Thinky', 'Cozy'] as const;

function visibleForFilter(filter: typeof filters[number], id: GameId) {
  if (filter === 'All games') return true;
  if (filter === 'Quick action') return ['mossfire', 'drift-courier', 'echo-shift'].includes(id);
  if (filter === 'Thinky') return ['hexhold', 'twenty-one-terrors', 'orbital-salvage', 'echo-shift'].includes(id);
  return ['dinky-diner', 'drift-courier'].includes(id);
}

export default function Portal({ save, onPlay }: PortalProps) {
  const [filter, setFilter] = useState<typeof filters[number]>('All games');
  const last = games.find((game) => game.id === save.lastPlayed) ?? null;
  const stamps = Object.values(save.stamps).reduce((count, earned) => count + (earned?.length ?? 0), 0);
  const visible = useMemo(() => games.filter((game) => visibleForFilter(filter, game.id)), [filter]);

  return (
    <main id="main-content">
      <section className="hero" aria-labelledby="hero-title">
        <div className="hero__copy">
          <span className="hero__issue">Issue 01 · seven machines · no tokens required</span>
          <h1 id="hero-title"><span>Small worlds.</span><strong>Big play.</strong></h1>
          <p>Seven handcrafted games built for the good kind of ten-minute detour. Pick a cabinet and make a little trouble.</p>
          <div className="hero__actions">
            <button className="button button--hero" onClick={() => onPlay(last?.id ?? 'mossfire')}>
              <Play fill="currentColor" size={20} />
              {last ? `Continue ${last.title}` : 'Play Mossfire'}
            </button>
            <a className="button button--paper" href="#machines">Browse all seven <ChevronRight size={18} /></a>
          </div>
        </div>
        <div className="hero__press" aria-hidden="true">
          <div className="hero__burst"><span>7</span> original games</div>
          <div className="hero__ticket"><small>Admit one</small><b>PLAY</b><span>CASPER · 2026</span></div>
          <div className="hero__mascot"><i /><b>!</b></div>
          <span className="hero__scribble">stay for one more run →</span>
        </div>
      </section>

      <section className="manifesto" aria-label="Arcade features">
        <div><Sparkles size={22} /><strong>Instant play</strong><span>No account. No install.</span></div>
        <div><Trophy size={22} /><strong>Real endings</strong><span>Every game can be mastered.</span></div>
        <div><Ticket size={22} /><strong>Ticket book</strong><span>{stamps}/21 stamps collected.</span></div>
      </section>

      <section className="machine-section" id="machines" aria-labelledby="machines-title">
        <header className="section-heading">
          <div>
            <span className="eyebrow">The machine room</span>
            <h2 id="machines-title">What are you in the mood to break?</h2>
          </div>
          <div className="filter-tabs" aria-label="Filter games">
            {filters.map((item) => (
              <button key={item} aria-pressed={filter === item} className={filter === item ? 'is-active' : ''} onClick={() => setFilter(item)}>{item}</button>
            ))}
          </div>
        </header>

        <div className="game-wall" aria-live="polite">
          {visible.map((game, index) => {
            const gameStamps = save.stamps[game.id] ?? [];
            const highScore = save.highScores[game.id];
            return (
              <article className={`game-poster game-poster--${index % 5}`} key={game.id} style={{ '--poster-ink': game.palette.ink, '--poster-field': game.palette.field, '--poster-accent': game.palette.accent, '--poster-spark': game.palette.spark } as React.CSSProperties}>
                <button className="game-poster__hit" onClick={() => onPlay(game.id)} aria-label={`Play ${game.title}`}>
                  <GameArtwork game={game.id} />
                  <span className="game-poster__number">0{games.findIndex((entry) => entry.id === game.id) + 1}</span>
                  <span className="game-poster__play"><Play fill="currentColor" size={18} /> Play</span>
                </button>
                <div className="game-poster__copy">
                  <div className="game-poster__title">
                    <span>{game.genre}</span>
                    <h3>{game.title}</h3>
                  </div>
                  <p>{game.description}</p>
                  <div className="game-poster__meta">
                    <span><Clock3 size={15} /> {game.playTime}</span>
                    <span><Gauge size={15} /> {game.difficulty}</span>
                    {highScore !== undefined && <span><Star size={15} /> {highScore.toLocaleString()}</span>}
                  </div>
                  <div className="stamp-row" aria-label={`${gameStamps.length} of 3 stamps earned`}>
                    {(['clear', 'skill', 'mastery'] as const).map((stamp) => <i key={stamp} className={gameStamps.includes(stamp) ? 'is-earned' : ''}>{stamp.slice(0, 1)}</i>)}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className="ticket-book" id="ticket-book" aria-labelledby="ticket-title">
        <div className="ticket-book__binding" aria-hidden="true">{Array.from({ length: 8 }, (_, i) => <i key={i} />)}</div>
        <div className="ticket-book__copy">
          <span className="eyebrow">Your pocket challenge</span>
          <h2 id="ticket-title">Fill the Ticket Book.</h2>
          <p>Clear, Skill, Mastery. Three stamps per cabinet. Collect all 21 to wake the little machine hiding under the counter.</p>
        </div>
        <div className="ticket-book__count"><strong>{stamps}</strong><span>of 21</span></div>
        <div className="ticket-book__stamps" aria-hidden="true">
          {games.map((game) => <span key={game.id} className={(save.stamps[game.id]?.length ?? 0) > 0 ? 'has-stamp' : ''}>{game.title.slice(0, 1)}</span>)}
        </div>
      </section>
    </main>
  );
}
